// ProductDetailPage.jsx
import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Footer from "./Footer";
import { AuthModal } from "./AuthModal";
import ReviewsTab from "../components/product/ReviewsTab";
import { useAuthContext } from "../hooks/AuthContext";
import config from "../config";

const { API_BASE, UPLOADS_URL, SHOP_UPLOADS_URL } = config;

async function fetchJSON(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

async function apiFetch(url, token, options = {}) {
  const res = await fetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Request failed");
  return data;
}

function formatPrice(val) {
  if (val === null || val === undefined) return null;
  return "R" + Number(val).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/* ── Product option unit types (BTU, KG gas cylinders, M aluminium tubes,
   KW heat pumps, M2 coolers) — keep in sync with admin/ShopProducts.php ── */
const UNIT_LABELS = { BTU: "BTU", KG: "KG", M: "MM", KW: "KW", M2: "M²" };
const OPTION_GROUP_LABEL = {
  BTU: "BTU Rating",
  KG: "Cylinder Size",
  M: "Tube Length",
  KW: "Capacity",
  M2: "Cooler Size",
};

function formatOptionValue(unitType, value) {
  const label = UNIT_LABELS[unitType] || unitType;
  const num = unitType === "BTU"
    ? Math.round(Number(value)).toLocaleString()
    : String(parseFloat(Number(value).toFixed(2)));
  return `${num} ${label}`;
}

/* Selector heading — e.g. "BTU Rating" for BTU-only products, "Cylinder Size"
   for KG-only, or a generic "Size" when a product mixes unit types. */
function optionsGroupLabel(options) {
  if (!options || !options.length) return "Size";
  const types = [...new Set(options.map((o) => o.unit_type))];
  return types.length === 1 ? (OPTION_GROUP_LABEL[types[0]] || "Size") : "Size";
}

/* Build WhatsApp enquiry link */
function buildWhatsAppLink(phoneNumber, productName, productUrl) {
  if (!phoneNumber) return null;
  const cleaned = phoneNumber.replace(/\D/g, "");
  const wa = cleaned.startsWith("0") ? "27" + cleaned.slice(1) : cleaned;
  const msg = `Hi, I'm interested in the *${productName}* and would like to enquire about pricing and availability.\n\nProduct link: ${productUrl}`;
  return `https://wa.me/${wa}?text=${encodeURIComponent(msg)}`;
}

/* ── Recently viewed (localStorage) ──
   Entries expire after RECENT_MAX_AGE_MS (7 days) so the list stays fresh. */
const RECENT_KEY = "recently_viewed";
const RECENT_MAX = 8;
const RECENT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function getRecentlyViewed() {
  try {
    const raw = JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");
    const now = Date.now();
    // Drop anything older than 7 days (or missing a timestamp, e.g. from
    // before this expiry logic existed) and persist the cleaned-up list.
    const fresh = raw.filter((p) => p.viewedAt && (now - p.viewedAt) < RECENT_MAX_AGE_MS);
    if (fresh.length !== raw.length) {
      localStorage.setItem(RECENT_KEY, JSON.stringify(fresh));
    }
    return fresh;
  } catch {
    return [];
  }
}

function pushRecentlyViewed(product) {
  const entry = {
    id: product.id, name: product.name, slug: product.slug,
    brand: product.brand, image1: product.image1,
    price: product.price, sale_price: product.sale_price,
    discount_percent: product.discount_percent, quantity: product.quantity,
    viewedAt: Date.now(),
  };
  const current = getRecentlyViewed().filter((p) => p.id !== product.id);
  const next = [entry, ...current].slice(0, RECENT_MAX);
  localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  return next;
}

/* ── Lightbox ── */
function Lightbox({ images, index, title, onClose, onNavigate }) {
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onNavigate(1);
      if (e.key === "ArrowLeft") onNavigate(-1);
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onClose, onNavigate]);

  return (
    <div className="pd-lightbox" onClick={onClose} role="dialog" aria-modal="true">
      <button className="pd-lightbox__close" onClick={onClose} aria-label="Close">
        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
          <path d="M4 4l12 12M16 4L4 16" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </button>
      {images.length > 1 && (
        <button className="pd-lightbox__nav pd-lightbox__nav--prev" onClick={(e) => { e.stopPropagation(); onNavigate(-1); }} aria-label="Previous">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M12 4l-6 6 6 6" stroke="#fff" strokeWidth="2" strokeLinecap="round" /></svg>
        </button>
      )}
      <img src={`${SHOP_UPLOADS_URL}/${images[index]}`} alt={`${title} – ${index + 1}`} className="pd-lightbox__img" onClick={(e) => e.stopPropagation()} />
      {images.length > 1 && (
        <button className="pd-lightbox__nav pd-lightbox__nav--next" onClick={(e) => { e.stopPropagation(); onNavigate(1); }} aria-label="Next">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M8 4l6 6-6 6" stroke="#fff" strokeWidth="2" strokeLinecap="round" /></svg>
        </button>
      )}
      <div className="pd-lightbox__footer">
        {title && <span className="pd-lightbox__title">{title}</span>}
        {images.length > 1 && <span className="pd-lightbox__counter">{index + 1} / {images.length}</span>}
      </div>
    </div>
  );
}

function SpecRow({ label, value }) {
  if (!value && value !== 0) return null;
  return (
    <tr className="pd-spec__row">
      <td className="pd-spec__label">{label}</td>
      <td className="pd-spec__value">{value}</td>
    </tr>
  );
}

function RelatedCard({ product }) {
  const navigate = useNavigate();
  const hasDiscount = product.sale_price && Number(product.sale_price) < Number(product.price);
  const hasOptions = (product.options_count || 0) > 0;
  return (
    <div className="pd-related-card" onClick={() => navigate(`/shop/${product.slug}`)}>
      <div className="pd-related-card__media">
        {product.image1 ? (
          <img src={`${SHOP_UPLOADS_URL}/${product.image1}`} alt={product.name} className="pd-related-card__img" loading="lazy" />
        ) : <div className="pd-related-card__fallback" />}
        {hasDiscount && <span className="pd-related-card__badge">−{product.discount_percent}%</span>}
      </div>
      <div className="pd-related-card__body">
        <p className="pd-related-card__name">{product.name}</p>
        <div className="pd-related-card__price">
          {hasDiscount ? (
            <><span className="pd-related-card__sale">{hasOptions ? "From " : ""}{formatPrice(product.sale_price)}</span><span className="pd-related-card__orig">{formatPrice(product.price)}</span></>
          ) : <span>{hasOptions ? "From " : ""}{formatPrice(product.price) ?? "Contact us"}</span>}
        </div>
        <p className="pd-related-card__vat">Excl. VAT</p>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════
   MAIN ProductDetailPage
════════════════════════════════════════════ */
function ProductDetailPage({ onAddToCart, cartItems = [], onOpenCart }) {
  const { slug } = useParams();
  const navigate = useNavigate();

  const { isLoggedIn, token, customer, login, logout } = useAuthContext();

  const [product,        setProduct]        = useState(null);
  const [related,        setRelated]        = useState([]);
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const [contact,        setContact]        = useState(null);
  const [loading,        setLoading]        = useState(true);
  const [error,          setError]          = useState(null);

  const [activeImg,    setActiveImg]    = useState(0);
  const [lightbox,     setLightbox]     = useState(null);
  const [wishlistIds,  setWishlistIds]  = useState([]);
  const [selectedOptionId, setSelectedOptionId] = useState(null); // selected product option id (BTU/KG/M/KW/M2)
  const [qty,          setQty]          = useState(1);
  const [activeTab,    setActiveTab]    = useState("description");
  const [addedToCart,  setAddedToCart]  = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  /* ── Reviews summary (for the "Reviews (n)" tab label) ── */
  const [reviewsSummary, setReviewsSummary] = useState({ count: 0, average: 0 });

  /* ── Auth modal ── */
  const [authModal,          setAuthModal]          = useState({ open: false, tab: "login" });
  const [pendingCartAdd,     setPendingCartAdd]     = useState(false);
  const [pendingWishlistAdd, setPendingWishlistAdd] = useState(false);

  /* ── Fetch product ── */
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setActiveImg(0);
    setQty(1);
    setAddedToCart(false);

    fetchJSON(`${API_BASE}/shop-product.php?slug=${encodeURIComponent(slug)}`)
      .then((data) => {
        if (cancelled) return;
        if (!data || data.error) { setError("Product not found."); setLoading(false); return; }
        setProduct(data.product);
        setRelated(data.related || []);
        // Default to the first option (if this product has size/variant options)
        const opts = data.product.options || [];
        setSelectedOptionId(opts.length > 0 ? opts[0].id : null);
        setLoading(false);
        setRecentlyViewed(pushRecentlyViewed(data.product));
      })
      .catch(() => {
        if (cancelled) return;
        setError("Failed to load product.");
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [slug]);

  /* ── Fetch review summary whenever the product changes ── */
  useEffect(() => {
    if (!product?.id) { setReviewsSummary({ count: 0, average: 0 }); return; }
    let cancelled = false;
    fetchJSON(`${API_BASE}/product-reviews.php?product_id=${product.id}`)
      .then((data) => {
        if (cancelled) return;
        setReviewsSummary(data.summary || { count: 0, average: 0 });
      })
      .catch(() => { if (!cancelled) setReviewsSummary({ count: 0, average: 0 }); });
    return () => { cancelled = true; };
  }, [product?.id]);

  /* ── Reset quantity whenever the selected size/option changes ── */
  useEffect(() => { setQty(1); }, [selectedOptionId]);

  /* ── Fetch company contact for WhatsApp ── */
  useEffect(() => {
    fetchJSON(`${API_BASE}/company-contact.php`)
      .then((data) => setContact(data || null))
      .catch(() => {});
  }, []);

  /* ── Load wishlist IDs ── */
  const loadWishlistIds = useCallback(() => {
    if (!isLoggedIn || !token) { setWishlistIds([]); return; }
    apiFetch(`${API_BASE}/customer-wishlist.php`, token)
      .then((data) => setWishlistIds((data.wishlist || []).map((p) => p.id)))
      .catch(() => setWishlistIds([]));
  }, [isLoggedIn, token]);

  useEffect(() => { loadWishlistIds(); }, [loadWishlistIds]);

  const doToggleWishlist = useCallback(() => {
    if (!product || !token) return;
    apiFetch(`${API_BASE}/customer-wishlist.php`, token, {
      method: "POST",
      body: JSON.stringify({ product_id: product.id }),
    })
      .then((data) => {
        setWishlistIds((prev) =>
          data.action === "added" ? [...prev, product.id] : prev.filter((x) => x !== product.id)
        );
      })
      .catch((e) => alert("Could not update wishlist: " + e.message));
  }, [product, token]);

  const handleWishlist = useCallback(() => {
    if (!isLoggedIn) {
      setPendingWishlistAdd(true);
      setAuthModal({ open: true, tab: "login" });
      return;
    }
    doToggleWishlist();
  }, [isLoggedIn, doToggleWishlist]);

  /* ── Option-aware derived values (computed below, after product null-checks) ── */
  const options = product?.options || [];
  const activeOption = options.find((o) => o.id === selectedOptionId) || null;

  const effectivePrice        = activeOption ? activeOption.price        : product?.price;
  const effectiveSalePrice    = activeOption ? activeOption.sale_price   : product?.sale_price;
  const effectiveQuantity     = activeOption ? activeOption.quantity     : product?.quantity;
  const effectiveSku          = activeOption ? (activeOption.sku || product?.sku) : product?.sku;

  const doAddToCart = useCallback(() => {
    if (!product || !effectiveQuantity) return;
    const cartItem = {
      ...product,
      // Give variant options a distinct cart-line identity so different sizes stack as separate lines
      id: activeOption ? `${product.id}-opt${activeOption.id}` : product.id,
      product_id: product.id,
      option_id: activeOption ? activeOption.id : null,
      // Kept for compatibility with any checkout/order code still reading this key
      btu_option_id: activeOption ? activeOption.id : null,
      unit_type: activeOption ? activeOption.unit_type : null,
      capacity_value: activeOption ? activeOption.capacity_value : null,
      name: activeOption
        ? `${product.name} — ${formatOptionValue(activeOption.unit_type, activeOption.capacity_value)}`
        : product.name,
      capacity_btu: activeOption ? (activeOption.unit_type === "BTU" ? activeOption.capacity_value : null) : product.capacity_btu,
      capacity_kw: activeOption ? (activeOption.unit_type === "KW" ? activeOption.capacity_value : null) : product.capacity_kw,
      sku: effectiveSku,
      price: effectivePrice,
      sale_price: effectiveSalePrice,
      quantity: effectiveQuantity,
      qty,
    };
    onAddToCart(cartItem);
    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 2000);
  }, [product, qty, onAddToCart, activeOption, effectivePrice, effectiveSalePrice, effectiveQuantity, effectiveSku]);

  const handleAddToCart = useCallback(() => {
    if (!isLoggedIn) {
      setPendingCartAdd(true);
      setAuthModal({ open: true, tab: "login" });
      return;
    }
    doAddToCart();
  }, [isLoggedIn, doAddToCart]);

  /* After successful login, run any pending action */
  const handleAuthLogin = useCallback((newToken, newCustomer) => {
    login(newToken, newCustomer);
    setAuthModal({ open: false, tab: "login" });
    if (pendingCartAdd) {
      setPendingCartAdd(false);
      setTimeout(() => doAddToCart(), 100);
    }
    if (pendingWishlistAdd) {
      setPendingWishlistAdd(false);
      setTimeout(() => doToggleWishlist(), 100);
    }
  }, [login, pendingCartAdd, doAddToCart, pendingWishlistAdd, doToggleWishlist]);

  const handleLogout = useCallback(() => {
    setUserMenuOpen(false);
    logout();
    navigate("/shop");
  }, [logout, navigate]);

  const navigateLightbox = useCallback((dir) => {
    setLightbox((lb) => lb && ({ ...lb, index: (lb.index + dir + lb.images.length) % lb.images.length }));
  }, []);

  const cartCount = cartItems?.reduce((s, i) => s + (i.qty || 0), 0) ?? 0;
  const wishlistCount = wishlistIds.length;
  const visibleRecentlyViewed = useMemo(
    () => (product ? recentlyViewed.filter((p) => p.id !== product.id) : recentlyViewed),
    [recentlyViewed, product]
  );

  /* ── Build WhatsApp enquiry URL ── */
  const waEnquiryLink = useMemo(() => {
    if (!product || !contact?.phone_number) return null;
    const productUrl = `${window.location.origin}/shop/${product.slug}`;
    return buildWhatsAppLink(contact.phone_number, product.name, productUrl);
  }, [product, contact]);

  if (loading) {
    return (
      <div className="pd">
        <div className="pd-loading">
          <div className="pd-spinner" aria-label="Loading…" />
          <p>Loading product…</p>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="pd">
        <div className="pd-error">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" aria-hidden="true">
            <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <p>{error || "Product not found."}</p>
          <button className="pd-error__btn" onClick={() => navigate("/shop")}>Back to shop</button>
        </div>
      </div>
    );
  }

  const images = [product.image1, product.image2, product.image3, product.image4].filter(Boolean);
  const hasDiscount = effectiveSalePrice && Number(effectiveSalePrice) < Number(effectivePrice);
  const inWishlist = wishlistIds.includes(product.id);
  const isLowStock = effectiveQuantity > 0 && effectiveQuantity <= (product.low_stock_alert || 5);
  const descLines = (product.description || "").split("\n").map((l) => l.replace(/^\s*-\s*/, "").trim()).filter(Boolean);

  const specGroups = [
    { label: "General", rows: [
      ["Brand", product.brand],
      ["Model number", product.model_number],
      ["SKU", effectiveSku],
      ["Condition", product.condition_type],
      ["Colour", product.colour],
      ["Warranty", product.warranty],
    ]},
    { label: "Technical", rows: [
      ["Product type", product.product_type],
      ...(activeOption
        ? [[OPTION_GROUP_LABEL[activeOption.unit_type] || "Size", formatOptionValue(activeOption.unit_type, activeOption.capacity_value)]]
        : [
            ["Capacity (BTU)", product.capacity_btu],
            ["Capacity (kW)", product.capacity_kw],
          ]),
      ["Energy rating", product.energy_rating],
      ["Coverage area", product.coverage_area_m2 ? `${product.coverage_area_m2} m²` : null],
      ["Installation included", product.installation_included ? "Yes" : product.installation_included === false ? "No" : null],
    ]},
    { label: "Dimensions & weight", rows: [
      ["Weight", product.weight ? `${product.weight} kg` : null],
      ["Dimensions (L×W×H)", product.dimensions],
    ]},
  ];

  const tabList = ["description", "specifications", "features", "reviews"];
  const tabLabels = {
    description: "Description",
    specifications: "Specifications",
    features: "Features",
    reviews: reviewsSummary.count > 0 ? `Reviews (${reviewsSummary.count})` : "Reviews",
  };

  return (
    <>
      <div className="pd">
        {/* ── HERO ── */}
        <div className="pd-hero">
          <div className="pd-hero__overlay" />
          <div className="pd-hero__content">
            <nav className="pd-bread" aria-label="Breadcrumb">
              <a href="/" className="pd-bread__link">Home</a>
              <span className="pd-bread__sep" aria-hidden="true">›</span>
              <Link to="/shop" className="pd-bread__link">Shop</Link>
              <span className="pd-bread__sep" aria-hidden="true">›</span>
              <span className="pd-bread__current">{product.name}</span>
            </nav>
            <h1 className="pd-hero__title">{product.name}</h1>
            {product.brand && <p className="pd-hero__subtitle">{product.brand}{product.category_name ? ` · ${product.category_name}` : ""}</p>}
          </div>
        </div>

        {/* ── TOOLBAR ── */}
        <div className="pd-toolbar-wrap">
          <div className="pd-toolbar">
            <div className="pd-toolbar__spacer" />
            <div className="pd-actions">
              {/* Wishlist */}
              <button
                className="pd-action-btn"
                onClick={() => navigate("/wishlist")}
                aria-label={`Wishlist (${wishlistCount} items)`}
                title="Wishlist"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                </svg>
                <span className="pd-action-btn__label">Wishlist</span>
                {wishlistCount > 0 && <span className="pd-action-btn__badge">{wishlistCount}</span>}
              </button>

              {/* Account */}
              {isLoggedIn ? (
                <div className="pd-user-menu">
                  <button
                    className="pd-action-btn pd-action-btn--account"
                    onClick={() => setUserMenuOpen((v) => !v)}
                    aria-expanded={userMenuOpen}
                    aria-label="Account menu"
                  >
                    <span className="pd-avatar">{customer?.first_name?.[0]?.toUpperCase() || "U"}</span>
                    <span className="pd-action-btn__label">{customer?.first_name || "Account"}</span>
                    <svg width="12" height="12" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.5" className={`pd-chevron ${userMenuOpen ? "pd-chevron--up" : ""}`}>
                      <path d="M4 7l6 6 6-6" strokeLinecap="round" />
                    </svg>
                  </button>
                  {userMenuOpen && (
                    <>
                      <div className="pd-user-backdrop" onClick={() => setUserMenuOpen(false)} />
                      <div className="pd-user-dropdown">
                        <div className="pd-user-dropdown__header">
                          <span className="pd-user-dropdown__name">{customer?.first_name} {customer?.last_name}</span>
                          <span className="pd-user-dropdown__email">{customer?.email}</span>
                        </div>
                        <button className="pd-user-dropdown__item" onClick={() => { setUserMenuOpen(false); navigate("/account"); }}>
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
                          My Profile
                        </button>
                        <button className="pd-user-dropdown__item" onClick={() => { setUserMenuOpen(false); navigate("/account?tab=orders"); }}>
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" /><rect x="9" y="3" width="6" height="4" rx="1" /></svg>
                          Order History
                        </button>
                        <div className="pd-user-dropdown__divider" />
                        <button className="pd-user-dropdown__item pd-user-dropdown__item--danger" onClick={handleLogout}>
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></svg>
                          Sign out
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <button className="pd-action-btn" onClick={() => setAuthModal({ open: true, tab: "login" })} aria-label="Sign in" title="Account">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
                  </svg>
                  <span className="pd-action-btn__label">Account</span>
                </button>
              )}

              {/* Cart */}
              <button
                className="pd-action-btn pd-action-btn--cart"
                onClick={() => onOpenCart ? onOpenCart() : navigate("/cart")}
                aria-label={`Cart (${cartCount} items)`}
                title="Cart"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" />
                  <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
                </svg>
                <span className="pd-action-btn__label">Cart</span>
                {cartCount > 0 && <span className="pd-action-btn__badge pd-action-btn__badge--cart">{cartCount}</span>}
              </button>
            </div>
          </div>
        </div>

        {/* ── MAIN LAYOUT ── */}
        <div className="pd-main">
          {/* ── GALLERY ── */}
          <div className="pd-gallery">
            {images.length > 1 && (
              <div className="pd-thumbs">
                {images.map((img, i) => (
                  <button key={i} className={`pd-thumb ${activeImg === i ? "pd-thumb--active" : ""}`}
                    onClick={() => setActiveImg(i)} aria-label={`View image ${i + 1}`}>
                    <img src={`${SHOP_UPLOADS_URL}/${img}`} alt="" />
                  </button>
                ))}
              </div>
            )}
            <div className="pd-gallery__main">
              {images.length > 0 ? (
                <button className="pd-gallery__img-btn"
                  onClick={() => setLightbox({ images, index: activeImg, title: product.name })}
                  aria-label="Open image fullscreen">
                  <img src={`${SHOP_UPLOADS_URL}/${images[activeImg]}`} alt={product.name} className="pd-gallery__img" />
                  <span className="pd-gallery__zoom" aria-hidden="true">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35M11 8v6M8 11h6" />
                    </svg>
                    Zoom
                  </span>
                </button>
              ) : <div className="pd-gallery__fallback" />}
              {images.length > 1 && (
                <>
                  <button className="pd-gallery__nav pd-gallery__nav--prev" onClick={() => setActiveImg((i) => (i - 1 + images.length) % images.length)} aria-label="Previous image">
                    <svg width="18" height="18" viewBox="0 0 20 20" fill="none"><path d="M12 4l-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
                  </button>
                  <button className="pd-gallery__nav pd-gallery__nav--next" onClick={() => setActiveImg((i) => (i + 1) % images.length)} aria-label="Next image">
                    <svg width="18" height="18" viewBox="0 0 20 20" fill="none"><path d="M8 4l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
                  </button>
                </>
              )}
            </div>
          </div>

          {/* ── PRODUCT INFO ── */}
          <div className="pd-info">
            <div className="pd-badges">
              {product.brand && <span className="pd-badge pd-badge--brand">{product.brand}</span>}
              {product.is_featured && <span className="pd-badge pd-badge--featured">Featured</span>}
              {hasDiscount && <span className="pd-badge pd-badge--sale">−{product.discount_percent}% OFF</span>}
              {product.condition_type && product.condition_type !== "new" && (
                <span className="pd-badge pd-badge--cond">{product.condition_type}</span>
              )}
            </div>

            <h1 className="pd-name">{product.name}</h1>
            {product.model_number && <p className="pd-model">Model: <code>{product.model_number}</code></p>}

            {reviewsSummary.count > 0 && (
              <button className="pd-rating-line" onClick={() => setActiveTab("reviews")}>
                <span className="pd-rating-line__stars" aria-hidden="true">
                  {"★".repeat(Math.round(reviewsSummary.average))}
                  {"☆".repeat(5 - Math.round(reviewsSummary.average))}
                </span>
                <span className="pd-rating-line__text">
                  {reviewsSummary.average.toFixed(1)} · {reviewsSummary.count} review{reviewsSummary.count !== 1 ? "s" : ""}
                </span>
              </button>
            )}

            <div className="pd-chips">
              {product.energy_rating && <span className="pd-chip"><strong>⚡</strong> {product.energy_rating}</span>}
              {activeOption ? (
                <span className="pd-chip">{formatOptionValue(activeOption.unit_type, activeOption.capacity_value)}</span>
              ) : (
                <>
                  {product.capacity_kw && <span className="pd-chip">{product.capacity_kw} kW</span>}
                  {product.capacity_btu && <span className="pd-chip">{Number(product.capacity_btu).toLocaleString()} BTU</span>}
                </>
              )}
              {product.coverage_area_m2 && <span className="pd-chip">{product.coverage_area_m2} m²</span>}
              {product.colour && <span className="pd-chip">{product.colour}</span>}
              {product.warranty && <span className="pd-chip">🛡 {product.warranty}</span>}
            </div>

            {/* ── SIZE / VARIANT SELECTOR (BTU, KG, M, KW, M²) ── */}
            {options.length > 0 && (
              <div className="pd-btu-selector">
                <p className="pd-btu-selector__label">{optionsGroupLabel(options)}</p>
                <div className="pd-btu-selector__options">
                  {options.map((opt) => (
                    <button
                      key={opt.id}
                      type="button"
                      className={`pd-btu-pill ${selectedOptionId === opt.id ? "pd-btu-pill--active" : ""} ${opt.quantity === 0 ? "pd-btu-pill--oos" : ""}`}
                      onClick={() => setSelectedOptionId(opt.id)}
                    >
                      {formatOptionValue(opt.unit_type, opt.capacity_value)}
                      {opt.quantity === 0 && <span className="pd-btu-pill__oos-tag">Out of stock</span>}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* ── PRICE + ENQUIRE ROW ── */}
            <div className="pd-price-row">
              <div className="pd-price-block">
                {hasDiscount ? (
                  <>
                    <span className="pd-price pd-price--sale">{formatPrice(effectiveSalePrice)}</span>
                    <span className="pd-price pd-price--orig">{formatPrice(effectivePrice)}</span>
                    <span className="pd-price__savings">Save {formatPrice(Number(effectivePrice) - Number(effectiveSalePrice))}</span>
                  </>
                ) : (
                  <span className="pd-price">{formatPrice(effectivePrice) ?? "Contact us for pricing"}</span>
                )}
                <span className="pd-price__vat-note">Price excl. VAT</span>
              </div>

              {/* WhatsApp Enquire Now */}
              {waEnquiryLink && (
                <a
                  href={waEnquiryLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="pd-wa-enquire"
                  aria-label="Enquire on WhatsApp"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                    <path d="M12 0C5.373 0 0 5.373 0 12c0 2.117.554 4.103 1.523 5.824L.057 23.928a.5.5 0 0 0 .6.633l6.313-1.434A11.94 11.94 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.818 9.818 0 0 1-5.001-1.369l-.358-.214-3.715.844.877-3.609-.234-.371A9.818 9.818 0 1 1 12 21.818z"/>
                  </svg>
                  Enquire Now
                </a>
              )}
            </div>

            <div className="pd-stock">
              {effectiveQuantity === 0 ? (
                <span className="pd-stock__out">
                  <svg width="14" height="14" viewBox="0 0 20 20" fill="#ef4444" aria-hidden="true"><circle cx="10" cy="10" r="10" /></svg>
                  Out of stock
                </span>
              ) : isLowStock ? (
                <span className="pd-stock__low">
                  <svg width="14" height="14" viewBox="0 0 20 20" fill="#f59e0b" aria-hidden="true"><circle cx="10" cy="10" r="10" /></svg>
                  Low stock — only {effectiveQuantity} left
                </span>
              ) : (
                <span className="pd-stock__in">
                  <svg width="14" height="14" viewBox="0 0 20 20" fill="#22c55e" aria-hidden="true"><circle cx="10" cy="10" r="10" /></svg>
                  In stock
                </span>
              )}
              {effectiveSku && <span className="pd-stock__sku">SKU: {effectiveSku}</span>}
            </div>

            {product.installation_included && (
              <div className="pd-install-badge">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#013d97" strokeWidth="2" aria-hidden="true">
                  <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
                </svg>
                Professional installation included
              </div>
            )}

            {/* Qty + CTA */}
            {effectiveQuantity > 0 && (
              <div className="pd-add-row">
                <div className="pd-qty">
                  <button className="pd-qty__btn" onClick={() => setQty((q) => Math.max(1, q - 1))} aria-label="Decrease quantity">−</button>
                  <span className="pd-qty__val" aria-live="polite">{qty}</span>
                  <button className="pd-qty__btn" onClick={() => setQty((q) => Math.min(effectiveQuantity, q + 1))} aria-label="Increase quantity">+</button>
                </div>
                <button className={`pd-cta ${addedToCart ? "pd-cta--added" : ""}`} onClick={handleAddToCart} disabled={addedToCart}>
                  {addedToCart ? (
                    <>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true"><path d="M20 6L9 17l-5-5" strokeLinecap="round" /></svg>
                      Added!
                    </>
                  ) : (
                    <>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" />
                        <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
                      </svg>
                      {isLoggedIn ? "Add to cart" : "Sign in to buy"}
                    </>
                  )}
                </button>
                <button
                  className={`pd-wish-btn ${inWishlist ? "pd-wish-btn--active" : ""}`}
                  onClick={handleWishlist}
                  aria-label={inWishlist ? "Remove from wishlist" : "Save to wishlist"}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill={inWishlist ? "#ef4444" : "none"} stroke={inWishlist ? "#ef4444" : "currentColor"} strokeWidth="2" aria-hidden="true">
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                  </svg>
                </button>
              </div>
            )}

            {/* Sign-in nudge for guests */}
            {!isLoggedIn && effectiveQuantity > 0 && (
              <div className="pd-auth-nudge">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <circle cx="12" cy="12" r="10"/><path d="M12 8v4m0 4h.01"/>
                </svg>
                <span>
                  <button className="pd-auth-nudge__link" onClick={() => setAuthModal({ open: true, tab: "login" })}>Sign in</button>
                  {" or "}
                  <button className="pd-auth-nudge__link" onClick={() => setAuthModal({ open: true, tab: "register" })}>create an account</button>
                  {" to add items to your cart."}
                </span>
              </div>
            )}

            {effectiveQuantity === 0 && (
              <div className="pd-oos-msg">
                {activeOption ? "This option is currently unavailable." : "This product is currently unavailable."}{" "}
                {waEnquiryLink ? (
                  <a href={waEnquiryLink} target="_blank" rel="noopener noreferrer">Contact us on WhatsApp</a>
                ) : (
                  <a href="/contact">Contact us</a>
                )}{" "}
                to enquire about availability.
              </div>
            )}

            <div className="pd-perks">
              <div className="pd-perk">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" /></svg>
                <span>Fast delivery</span>
              </div>
              <div className="pd-perk">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
                <span>Secure checkout</span>
              </div>
              <div className="pd-perk">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></svg>
                <span>Local support</span>
              </div>
            </div>
          </div>
        </div>

        {/* ── TABS ── */}
        <div className="pd-tabs-wrap">
          <div className="pd-tabs-inner">
            <div className="pd-tabs" role="tablist">
              {tabList.map((tab) => (
                <button key={tab} role="tab" aria-selected={activeTab === tab}
                  className={`pd-tab ${activeTab === tab ? "pd-tab--active" : ""}`}
                  onClick={() => setActiveTab(tab)}>
                  {tabLabels[tab]}
                </button>
              ))}
            </div>
          </div>
          <div className="pd-tab-content">
            {activeTab === "description" && (
              <div className="pd-desc">
                {descLines.length > 0 ? (
                  <ul className="pd-desc__list">
                    {descLines.map((line, i) => <li key={i}>{line}</li>)}
                  </ul>
                ) : <p className="pd-desc__empty">No description available.</p>}
              </div>
            )}
            {activeTab === "specifications" && (
              <div className="pd-specs">
                {specGroups.map((group) => {
                  const validRows = group.rows.filter(([, v]) => v !== null && v !== undefined && v !== "");
                  if (!validRows.length) return null;
                  return (
                    <div key={group.label} className="pd-spec-group">
                      <h3 className="pd-spec-group__title">{group.label}</h3>
                      <table className="pd-spec">
                        <tbody>
                          {validRows.map(([label, value]) => <SpecRow key={label} label={label} value={value} />)}
                        </tbody>
                      </table>
                    </div>
                  );
                })}
              </div>
            )}
            {activeTab === "features" && (
              <div className="pd-features">
                {product.features ? (
                  <ul className="pd-features__list">
                    {String(product.features).split("\n").map((f) => f.trim()).filter(Boolean).map((f, i) => (
                      <li key={i} className="pd-features__item">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#029add" strokeWidth="2.5" aria-hidden="true">
                          <path d="M20 6L9 17l-5-5" strokeLinecap="round" />
                        </svg>
                        {f.replace(/^-\s*/, "")}
                      </li>
                    ))}
                  </ul>
                ) : <p className="pd-desc__empty">No features listed.</p>}
              </div>
            )}
            {activeTab === "reviews" && (
              <ReviewsTab
                productId={product.id}
                summary={reviewsSummary}
                onSummaryChange={setReviewsSummary}
              />
            )}
          </div>
        </div>

        {/* ── RELATED PRODUCTS ── */}
        {related.length > 0 && (
          <div className="pd-related">
            <div className="pd-related__inner">
              <h2 className="pd-related__heading">You may also like</h2>
              <div className="pd-related__grid">
                {related.slice(0, 4).map((p) => <RelatedCard key={p.id} product={p} />)}
              </div>
            </div>
          </div>
        )}

        {/* ── RECENTLY VIEWED ── */}
        {visibleRecentlyViewed.length > 0 && (
          <div className="pd-recent">
            <div className="pd-recent__inner">
              <h2 className="pd-recent__heading">Recently viewed</h2>
              <div className="pd-recent__grid">
                {visibleRecentlyViewed.map((p) => <RelatedCard key={`recent-${p.id}`} product={p} />)}
              </div>
            </div>
          </div>
        )}

        {/* Lightbox */}
        {lightbox && (
          <Lightbox images={lightbox.images} index={lightbox.index} title={lightbox.title}
            onClose={() => setLightbox(null)} onNavigate={navigateLightbox} />
        )}

        {/* ── AUTH MODAL ── */}
        <AuthModal
          isOpen={authModal.open}
          initialTab={authModal.tab}
          onClose={() => {
            setAuthModal((s) => ({ ...s, open: false }));
            setPendingCartAdd(false);
            setPendingWishlistAdd(false);
          }}
          onLogin={handleAuthLogin}
        />

        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;600;700;800;900&display=swap');
          .pd *, .pd *::before, .pd *::after { box-sizing: border-box; margin: 0; padding: 0; }
          .pd a { text-decoration: none; }
          .pd { font-family: 'Roboto', sans-serif; background: #f5f7fa; }

          /* ── HERO ── */
          .pd-hero { position: relative; height: 170px; background: linear-gradient(135deg, #011546 0%, #013d97 60%, #0255cc 100%); overflow: hidden; }
          .pd-hero__overlay { position: absolute; inset: 0; background: linear-gradient(to bottom, transparent 40%, rgba(1,15,50,0.3) 100%); }
          .pd-hero__content { position: absolute; inset: 0; display: flex; flex-direction: column; justify-content: flex-end; padding: 22px 40px; max-width: 1280px; margin: 0 auto; left: 0; right: 0; }
          .pd-bread { display: flex; align-items: center; gap: 6px; margin-bottom: 8px; flex-wrap: wrap; }
          .pd-bread__link { font-size: 12.5px; font-weight: 600; color: rgba(255,255,255,0.8); background: rgba(255,255,255,0.12); padding: 3px 10px; border-radius: 4px; transition: background .2s; }
          .pd-bread__link:hover { background: rgba(255,255,255,.22); }
          .pd-bread__sep { color: rgba(255,255,255,0.4); font-size: 13px; }
          .pd-bread__current { font-size: 12.5px; color: rgba(255,255,255,0.7); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 340px; }
          .pd-hero__title { font-size: clamp(18px, 2.4vw, 26px); font-weight: 900; color: #fff; line-height: 1.2; }
          .pd-hero__subtitle { font-size: 13px; color: rgba(255,255,255,.7); margin-top: 4px; }

          /* ── TOOLBAR ── */
          .pd-toolbar-wrap { background: #fff; border-bottom: 1px solid #e2e8f0; box-shadow: 0 2px 8px rgba(1,21,70,.06); }
          .pd-toolbar { max-width: 1280px; margin: 0 auto; padding: 10px 40px; display: flex; align-items: center; gap: 10px; }
          .pd-toolbar__spacer { flex: 1; }
          .pd-actions { display: flex; align-items: center; gap: 4px; flex-shrink: 0; }
          .pd-action-btn { position: relative; display: flex; align-items: center; gap: 6px; padding: 8px 12px; border: 1.5px solid #e2e8f0; background: #fff; border-radius: 8px; cursor: pointer; font-family: inherit; font-size: 13px; font-weight: 700; color: #374151; transition: border-color .18s, background .18s, color .18s; white-space: nowrap; }
          .pd-action-btn:hover { border-color: #013d97; color: #013d97; background: #f0f5ff; }
          .pd-action-btn--cart { background: #013d97; color: #fff; border-color: #013d97; }
          .pd-action-btn--cart:hover { background: #022d75; border-color: #022d75; color: #fff; }
          .pd-action-btn__badge { position: absolute; top: -6px; right: -6px; min-width: 18px; height: 18px; border-radius: 9px; background: #ef4444; color: #fff; font-size: 10px; font-weight: 800; display: flex; align-items: center; justify-content: center; padding: 0 4px; border: 2px solid #fff; }
          .pd-action-btn__badge--cart { background: #f59e0b; }
          .pd-action-btn__label { font-size: 13px; }
          .pd-avatar { width: 22px; height: 22px; border-radius: 50%; background: #013d97; color: #fff; font-size: 11px; font-weight: 900; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
          .pd-action-btn--account { border-color: #c8d8f5; background: #eef3fb; color: #013d97; }
          .pd-action-btn--account:hover { background: #dce9f9; border-color: #013d97; }
          .pd-chevron { transition: transform .18s; }
          .pd-chevron--up { transform: rotate(180deg); }
          .pd-user-menu { position: relative; }
          .pd-user-backdrop { position: fixed; inset: 0; z-index: 90; }
          .pd-user-dropdown { position: absolute; top: calc(100% + 8px); right: 0; min-width: 220px; background: #fff; border: 1.5px solid #e2e8f0; border-radius: 10px; box-shadow: 0 8px 24px rgba(1,21,70,.14); z-index: 200; overflow: hidden; animation: pd-drop-in .16s ease; }
          @keyframes pd-drop-in { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: none; } }
          .pd-user-dropdown__header { padding: 14px 16px 12px; border-bottom: 1px solid #f0f2f5; }
          .pd-user-dropdown__name { display: block; font-size: 14px; font-weight: 800; color: #1a2236; }
          .pd-user-dropdown__email { display: block; font-size: 12px; color: #8890a0; margin-top: 2px; }
          .pd-user-dropdown__item { display: flex; align-items: center; gap: 10px; width: 100%; padding: 11px 16px; background: none; border: none; cursor: pointer; font-family: inherit; font-size: 13.5px; font-weight: 600; color: #374151; text-align: left; transition: background .15s; }
          .pd-user-dropdown__item:hover { background: #f5f7fa; color: #013d97; }
          .pd-user-dropdown__item--danger { color: #991b1b; }
          .pd-user-dropdown__item--danger:hover { background: #fef2f2; color: #991b1b; }
          .pd-user-dropdown__divider { border-top: 1px solid #f0f2f5; }

          /* ── MAIN LAYOUT ── */
          .pd-main { max-width: 1280px; margin: 0 auto; padding: 40px 40px 20px; display: grid; grid-template-columns: 1fr 1fr; gap: 48px; align-items: start; }

          /* ── GALLERY ── */
          .pd-gallery { display: flex; gap: 12px; }
          .pd-thumbs { display: flex; flex-direction: column; gap: 8px; }
          .pd-thumb { width: 68px; height: 68px; border-radius: 8px; overflow: hidden; border: 2px solid transparent; background: #fff; cursor: pointer; padding: 2px; flex-shrink: 0; transition: border-color .18s; }
          .pd-thumb img { width: 100%; height: 100%; object-fit: cover; border-radius: 5px; }
          .pd-thumb--active { border-color: #013d97; }
          .pd-thumb:hover:not(.pd-thumb--active) { border-color: #c8d8f5; }
          .pd-gallery__main { position: relative; flex: 1; border-radius: 12px; overflow: hidden; background: #fff; box-shadow: 0 2px 12px rgba(1,21,70,.08); }
          .pd-gallery__img-btn { display: block; width: 100%; border: none; background: none; padding: 0; cursor: zoom-in; position: relative; }
          .pd-gallery__img { width: 100%; aspect-ratio: 1/1; object-fit: cover; display: block; }
          .pd-gallery__fallback { width: 100%; aspect-ratio: 1/1; background: linear-gradient(135deg, #e8edf6, #d4ddf0); }
          .pd-gallery__zoom { position: absolute; bottom: 14px; right: 14px; display: flex; align-items: center; gap: 6px; background: rgba(1,21,70,.7); color: #fff; padding: 7px 12px; border-radius: 6px; font-size: 12.5px; font-weight: 700; pointer-events: none; }
          .pd-gallery__nav { position: absolute; top: 50%; transform: translateY(-50%); width: 36px; height: 36px; border-radius: 50%; background: rgba(255,255,255,.9); border: none; cursor: pointer; display: flex; align-items: center; justify-content: center; box-shadow: 0 2px 8px rgba(0,0,0,.12); color: #013d97; transition: background .2s; }
          .pd-gallery__nav:hover { background: #fff; }
          .pd-gallery__nav--prev { left: 12px; }
          .pd-gallery__nav--next { right: 12px; }

          /* ── PRODUCT INFO ── */
          .pd-info { display: flex; flex-direction: column; gap: 18px; }
          .pd-badges { display: flex; flex-wrap: wrap; gap: 6px; }
          .pd-badge { display: inline-block; padding: 4px 12px; border-radius: 4px; font-size: 11px; font-weight: 800; letter-spacing: .05em; text-transform: uppercase; }
          .pd-badge--brand { background: #eef3fb; color: #013d97; }
          .pd-badge--featured { background: #013d97; color: #fff; }
          .pd-badge--sale { background: #ef4444; color: #fff; }
          .pd-badge--cond { background: #fef3c7; color: #92400e; }
          .pd-name { font-size: clamp(20px, 2.5vw, 30px); font-weight: 900; color: #1a2236; line-height: 1.2; }
          .pd-model { font-size: 13px; color: #8890a0; }
          .pd-model code { background: #f0f2f5; padding: 2px 6px; border-radius: 4px; font-size: 12px; color: #1a2236; }
          .pd-rating-line { display: inline-flex; align-items: center; gap: 8px; background: none; border: none; cursor: pointer; padding: 0; width: fit-content; }
          .pd-rating-line__stars { color: #f59e0b; font-size: 15px; letter-spacing: 1px; }
          .pd-rating-line__text { font-size: 13px; font-weight: 600; color: #5a6478; text-decoration: underline; text-underline-offset: 2px; }
          .pd-rating-line:hover .pd-rating-line__text { color: #013d97; }
          .pd-chips { display: flex; flex-wrap: wrap; gap: 6px; }
          .pd-chip { display: inline-flex; align-items: center; gap: 5px; padding: 5px 12px; background: #f0f2f5; border-radius: 20px; font-size: 12.5px; font-weight: 600; color: #374151; }
          .pd-chip strong { color: #013d97; }

          /* ── SIZE / VARIANT SELECTOR ── */
          .pd-btu-selector { display: flex; flex-direction: column; gap: 8px; }
          .pd-btu-selector__label { font-size: 12px; font-weight: 800; text-transform: uppercase; letter-spacing: .06em; color: #8890a0; }
          .pd-btu-selector__options { display: flex; flex-wrap: wrap; gap: 8px; }
          .pd-btu-pill { display: flex; flex-direction: column; align-items: center; gap: 2px; padding: 9px 16px; border: 1.5px solid #e2e8f0; border-radius: 8px; background: #fff; cursor: pointer; font-family: inherit; font-size: 13.5px; font-weight: 700; color: #374151; transition: border-color .18s, background .18s, color .18s; }
          .pd-btu-pill:hover { border-color: #c8d8f5; }
          .pd-btu-pill--active { border-color: #013d97; background: #eef3fb; color: #013d97; }
          .pd-btu-pill--oos { opacity: .6; }
          .pd-btu-pill__oos-tag { font-size: 10px; font-weight: 700; color: #ef4444; text-transform: uppercase; letter-spacing: .04em; }

          /* ── PRICE ROW ── */
          .pd-price-row { display: flex; align-items: flex-start; gap: 14px; flex-wrap: wrap; }
          .pd-price-block { display: flex; align-items: baseline; gap: 10px; flex-wrap: wrap; flex: 1; min-width: 0; }
          .pd-price { font-size: 30px; font-weight: 900; color: #1a2236; }
          .pd-price--sale { color: #013d97; }
          .pd-price--orig { font-size: 18px; font-weight: 500; color: #9ca3af; text-decoration: line-through; }
          .pd-price__savings { font-size: 13px; font-weight: 700; background: #ecfdf5; color: #065f46; padding: 4px 10px; border-radius: 20px; }
          .pd-price__vat-note { flex-basis: 100%; font-size: 12px; font-weight: 600; color: #8890a0; }

          /* ── WHATSAPP ENQUIRE BUTTON ── */
          .pd-wa-enquire { display: inline-flex; align-items: center; gap: 8px; padding: 10px 18px; background: #25d366; color: #fff; border-radius: 8px; font-family: 'Roboto', sans-serif; font-size: 13.5px; font-weight: 800; text-decoration: none; white-space: nowrap; flex-shrink: 0; transition: background .18s, transform .18s; box-shadow: 0 2px 8px rgba(37,211,102,.3); }
          .pd-wa-enquire:hover { background: #1da851; transform: translateY(-1px); box-shadow: 0 4px 14px rgba(37,211,102,.4); }

          .pd-stock { display: flex; align-items: center; gap: 16px; flex-wrap: wrap; }
          .pd-stock__in, .pd-stock__out, .pd-stock__low { display: flex; align-items: center; gap: 7px; font-size: 13.5px; font-weight: 700; }
          .pd-stock__in { color: #065f46; }
          .pd-stock__out { color: #991b1b; }
          .pd-stock__low { color: #92400e; }
          .pd-stock__sku { font-size: 12.5px; color: #8890a0; background: #f0f2f5; padding: 3px 9px; border-radius: 4px; }
          .pd-install-badge { display: inline-flex; align-items: center; gap: 8px; background: #eef3fb; border: 1px solid #c8d8f5; color: #013d97; padding: 8px 14px; border-radius: 8px; font-size: 13px; font-weight: 700; }
          .pd-add-row { display: flex; align-items: center; gap: 10px; }
          .pd-qty { display: flex; align-items: center; border: 1.5px solid #e2e8f0; border-radius: 8px; overflow: hidden; }
          .pd-qty__btn { width: 38px; height: 46px; border: none; background: #f8f9fb; cursor: pointer; font-size: 18px; color: #1a2236; font-weight: 700; transition: background .18s; display: flex; align-items: center; justify-content: center; }
          .pd-qty__btn:hover { background: #e8edf6; }
          .pd-qty__val { min-width: 36px; text-align: center; font-size: 14px; font-weight: 700; color: #1a2236; padding: 0 4px; }
          .pd-cta { flex: 1; display: flex; align-items: center; justify-content: center; gap: 9px; background: #013d97; color: #fff; border: none; border-radius: 8px; padding: 14px 20px; font-family: inherit; font-size: 14.5px; font-weight: 800; cursor: pointer; transition: background .2s; }
          .pd-cta:hover:not(:disabled) { background: #022d75; }
          .pd-cta--added { background: #16a34a; }
          .pd-cta--added:hover { background: #15803d; }
          .pd-wish-btn { width: 46px; height: 46px; border-radius: 8px; border: 1.5px solid #e2e8f0; background: #fff; cursor: pointer; display: flex; align-items: center; justify-content: center; flex-shrink: 0; transition: border-color .18s, background .18s; }
          .pd-wish-btn:hover { border-color: #ef4444; background: #fff1f1; }
          .pd-wish-btn--active { border-color: #ef4444; background: #fff1f1; }
          .pd-auth-nudge { display: flex; align-items: flex-start; gap: 8px; background: #fffbeb; border: 1px solid #fde68a; color: #92400e; padding: 10px 14px; border-radius: 8px; font-size: 13px; line-height: 1.5; }
          .pd-auth-nudge svg { flex-shrink: 0; margin-top: 1px; color: #d97706; }
          .pd-auth-nudge__link { background: none; border: none; cursor: pointer; font-family: inherit; font-size: inherit; font-weight: 700; color: #013d97; padding: 0; text-decoration: underline; }
          .pd-oos-msg { background: #fef2f2; border: 1px solid #fecaca; color: #991b1b; padding: 12px 16px; border-radius: 8px; font-size: 13.5px; }
          .pd-oos-msg a { color: #013d97; font-weight: 700; }
          .pd-perks { display: flex; gap: 20px; padding: 16px 0; border-top: 1px solid #f0f2f5; margin-top: 4px; flex-wrap: wrap; }
          .pd-perk { display: flex; align-items: center; gap: 7px; font-size: 12.5px; color: #5a6478; font-weight: 600; }
          .pd-perk svg { color: #029add; }

          /* ── TABS ── */
          .pd-tabs-wrap { max-width: 1280px; margin: 28px auto 0; padding: 0 40px; }
          .pd-tabs-inner { border-bottom: 2px solid #f0f2f5; }
          .pd-tabs { display: flex; gap: 2px; flex-wrap: wrap; }
          .pd-tab { padding: 13px 22px; background: none; border: none; cursor: pointer; font-family: inherit; font-size: 14px; font-weight: 700; color: #8890a0; border-bottom: 2px solid transparent; margin-bottom: -2px; transition: color .18s, border-color .18s; }
          .pd-tab:hover { color: #013d97; }
          .pd-tab--active { color: #013d97; border-bottom-color: #013d97; }
          .pd-tab-content { background: #fff; border-radius: 0 0 10px 10px; padding: 30px; box-shadow: 0 2px 8px rgba(1,21,70,.05); margin-bottom: 40px; }
          .pd-desc__list { list-style: none; display: flex; flex-direction: column; gap: 10px; }
          .pd-desc__list li { position: relative; padding-left: 18px; font-size: 14.5px; color: #374151; line-height: 1.7; }
          .pd-desc__list li::before { content: "–"; position: absolute; left: 0; color: #029add; font-weight: 700; }
          .pd-desc__empty { color: #9ca3af; font-size: 14px; }
          .pd-specs { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 24px; }
          .pd-spec-group__title { font-size: 12px; font-weight: 800; text-transform: uppercase; letter-spacing: .06em; color: #8890a0; margin-bottom: 10px; }
          .pd-spec { width: 100%; border-collapse: collapse; font-size: 13.5px; }
          .pd-spec__row { border-bottom: 1px solid #f0f2f5; }
          .pd-spec__row:last-child { border-bottom: none; }
          .pd-spec__label { padding: 9px 0; color: #8890a0; font-weight: 600; width: 48%; }
          .pd-spec__value { padding: 9px 0; color: #1a2236; font-weight: 700; }
          .pd-features__list { list-style: none; display: flex; flex-direction: column; gap: 12px; }
          .pd-features__item { display: flex; align-items: flex-start; gap: 10px; font-size: 14.5px; color: #374151; line-height: 1.6; }
          .pd-features__item svg { flex-shrink: 0; margin-top: 2px; }

          /* ── RELATED / RECENT ── */
          .pd-related { background: #f5f7fa; padding: 40px 0 40px; }
          .pd-related__inner { max-width: 1280px; margin: 0 auto; padding: 0 40px; }
          .pd-related__heading { font-size: 20px; font-weight: 800; color: #1a2236; margin-bottom: 24px; }
          .pd-related__grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 18px; }
          .pd-recent { background: #eef1f6; padding: 40px 0 80px; border-top: 1px solid #e2e8f0; }
          .pd-recent__inner { max-width: 1280px; margin: 0 auto; padding: 0 40px; }
          .pd-recent__heading { font-size: 20px; font-weight: 800; color: #1a2236; margin-bottom: 24px; }
          .pd-recent__grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 18px; }
          .pd-related-card { background: #fff; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 8px rgba(1,21,70,.07); cursor: pointer; transition: transform .2s, box-shadow .2s; }
          .pd-related-card:hover { transform: translateY(-2px); box-shadow: 0 6px 18px rgba(1,21,70,.12); }
          .pd-related-card__media { position: relative; aspect-ratio: 1/1; overflow: hidden; background: #f0f2f5; }
          .pd-related-card__img { width: 100%; height: 100%; object-fit: cover; transition: transform .4s ease; }
          .pd-related-card:hover .pd-related-card__img { transform: scale(1.04); }
          .pd-related-card__fallback { width: 100%; height: 100%; background: linear-gradient(135deg, #e8edf6, #d4ddf0); }
          .pd-related-card__badge { position: absolute; top: 8px; left: 8px; background: #ef4444; color: #fff; font-size: 10px; font-weight: 800; padding: 3px 8px; border-radius: 4px; }
          .pd-related-card__body { padding: 12px; }
          .pd-related-card__name { font-size: 13px; font-weight: 700; color: #1a2236; line-height: 1.35; margin-bottom: 6px; }
          .pd-related-card__price { display: flex; align-items: baseline; gap: 6px; font-size: 14px; font-weight: 800; color: #013d97; }
          .pd-related-card__sale { color: #013d97; }
          .pd-related-card__orig { font-size: 12px; font-weight: 500; color: #9ca3af; text-decoration: line-through; }
          .pd-related-card__vat { font-size: 10.5px; color: #8890a0; margin-top: 2px; }

          /* ── LIGHTBOX ── */
          .pd-lightbox { position: fixed; inset: 0; z-index: 1000; background: rgba(5,10,25,.92); display: flex; align-items: center; justify-content: center; padding: 40px; }
          .pd-lightbox__img { max-width: min(90vw, 1100px); max-height: 82vh; object-fit: contain; border-radius: 4px; box-shadow: 0 10px 40px rgba(0,0,0,.5); }
          .pd-lightbox__close { position: absolute; top: 20px; right: 20px; width: 40px; height: 40px; border-radius: 50%; background: rgba(255,255,255,.12); border: none; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: background .2s; }
          .pd-lightbox__close:hover { background: rgba(255,255,255,.25); }
          .pd-lightbox__nav { position: absolute; top: 50%; transform: translateY(-50%); width: 48px; height: 48px; border-radius: 50%; background: rgba(255,255,255,.12); border: none; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: background .2s; }
          .pd-lightbox__nav:hover { background: rgba(255,255,255,.25); }
          .pd-lightbox__nav--prev { left: 20px; }
          .pd-lightbox__nav--next { right: 20px; }
          .pd-lightbox__footer { position: absolute; bottom: 24px; left: 50%; transform: translateX(-50%); display: flex; align-items: center; gap: 14px; color: #fff; font-size: 13px; }
          .pd-lightbox__title { font-weight: 700; }
          .pd-lightbox__counter { background: rgba(255,255,255,.14); padding: 6px 14px; border-radius: 20px; }

          /* ── LOADING / ERROR ── */
          .pd-loading, .pd-error { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 16px; min-height: 50vh; padding: 40px; text-align: center; color: #8890a0; font-size: 14px; }
          .pd-spinner { width: 36px; height: 36px; border: 3px solid #e2e8f0; border-top-color: #013d97; border-radius: 50%; animation: pd-spin .75s linear infinite; }
          @keyframes pd-spin { to { transform: rotate(360deg); } }
          .pd-error svg { color: #c0c8d8; }
          .pd-error__btn { background: #013d97; color: #fff; border: none; padding: 11px 24px; border-radius: 8px; font-family: inherit; font-size: 14px; font-weight: 700; cursor: pointer; margin-top: 8px; }

          /* ════════════════════════════════════════════
             AUTH MODAL STYLES — injected here so they
             render correctly in this page's context
          ════════════════════════════════════════════ */
          .am-backdrop {
            position: fixed; inset: 0; z-index: 9000;
            background: rgba(1,15,50,.65);
            backdrop-filter: blur(6px);
            display: flex; align-items: center; justify-content: center;
            padding: 20px;
            font-family: 'Roboto', sans-serif;
          }
          .am-modal {
            position: relative; background: #fff; border-radius: 20px;
            width: 100%; max-width: 440px; max-height: 92vh; overflow-y: auto;
            box-shadow: 0 0 0 1px rgba(1,61,151,.08), 0 8px 16px rgba(1,15,50,.10), 0 32px 80px rgba(1,15,50,.22);
            animation: am-slide-in .24s cubic-bezier(.34,1.3,.64,1);
          }
          @keyframes am-slide-in {
            from { opacity: 0; transform: translateY(16px) scale(.96); }
            to   { opacity: 1; transform: translateY(0) scale(1); }
          }
          .am-header { display: flex; align-items: center; justify-content: space-between; padding: 22px 24px 0; }
          .am-brand { display: flex; align-items: center; gap: 10px; }
          .am-brand__icon {
            width: 36px; height: 36px; border-radius: 10px;
            background: linear-gradient(135deg, #013d97, #0255cc);
            display: flex; align-items: center; justify-content: center;
            flex-shrink: 0; box-shadow: 0 2px 8px rgba(1,61,151,.35);
          }
          .am-brand__name { font-size: 17px; font-weight: 800; color: #0f1c38; letter-spacing: -.02em; }
          .am-close {
            width: 32px; height: 32px; border-radius: 8px;
            border: 1.5px solid #e8ecf3; background: #f7f9fc;
            cursor: pointer; display: flex; align-items: center; justify-content: center;
            color: #6b7a99; transition: all .15s;
          }
          .am-close:hover { background: #fee2e2; border-color: #fecaca; color: #dc2626; }
          .am-tabs {
            display: flex; margin: 20px 24px 0;
            background: #f0f4fa; border-radius: 10px; padding: 4px;
          }
          .am-tab {
            flex: 1; padding: 9px 0; background: none; border: none; border-radius: 7px;
            font-family: 'Roboto', sans-serif; font-size: 13.5px; font-weight: 700;
            color: #7a88a8; cursor: pointer; transition: all .18s;
          }
          .am-tab:hover:not(.am-tab--active) { color: #013d97; }
          .am-tab--active {
            background: #fff; color: #013d97;
            box-shadow: 0 1px 4px rgba(1,21,70,.12), 0 0 0 1px rgba(1,61,151,.08);
          }
          .am-body { padding: 22px 24px 28px; }
          .am-form { display: flex; flex-direction: column; gap: 16px; }
          .am-form__intro { font-size: 13.5px; color: #6b7a99; line-height: 1.55; }
          .am-error {
            background: #fef2f2; border: 1px solid #fecaca; color: #991b1b;
            padding: 11px 14px; border-radius: 10px; font-size: 13px; font-weight: 500; line-height: 1.45;
          }
          .am-steps { display: flex; align-items: center; }
          .am-step { display: flex; align-items: center; gap: 7px; }
          .am-step__dot {
            width: 24px; height: 24px; border-radius: 50%;
            background: #e8ecf3; color: #7a88a8;
            font-size: 11px; font-weight: 800;
            display: flex; align-items: center; justify-content: center; flex-shrink: 0;
          }
          .am-step--active .am-step__dot { background: #013d97; color: #fff; box-shadow: 0 0 0 3px rgba(1,61,151,.15); }
          .am-step__label { font-size: 12.5px; font-weight: 700; color: #b0bac8; }
          .am-step--active .am-step__label { color: #013d97; }
          .am-step__line { flex: 1; height: 2px; background: #e8ecf3; margin: 0 10px; border-radius: 1px; }
          .am-optional-notice {
            display: flex; align-items: center; gap: 7px;
            background: #f0f7ff; border: 1px solid #bfdbfe; color: #1e40af;
            padding: 10px 13px; border-radius: 8px; font-size: 12.5px; font-weight: 500;
          }
          .am-field { display: flex; flex-direction: column; gap: 6px; flex: 1; min-width: 0; }
          .am-field__label { font-size: 12px; font-weight: 700; color: #374151; letter-spacing: .02em; text-transform: uppercase; }
          .am-field__req { color: #ef4444; }
          .am-field__input {
            width: 100%; border: 1.5px solid #e2e8f0; border-radius: 10px;
            padding: 11px 14px; font-family: 'Roboto', sans-serif; font-size: 14px;
            color: #0f1c38; background: #fafbfd; outline: none;
            transition: border-color .18s, box-shadow .18s; box-sizing: border-box;
          }
          .am-field__input:focus { border-color: #013d97; background: #fff; box-shadow: 0 0 0 3px rgba(1,61,151,.10); }
          .am-field__input::placeholder { color: #b8c2d8; }
          .am-row { display: flex; gap: 12px; }
          .am-btn-row { display: flex; gap: 10px; align-items: center; }
          .am-btn {
            display: inline-flex; align-items: center; justify-content: center; gap: 7px;
            padding: 12px 20px; border-radius: 10px; border: none; cursor: pointer;
            font-family: 'Roboto', sans-serif; font-size: 14px; font-weight: 700;
            transition: all .18s; width: 100%;
          }
          .am-btn:disabled { opacity: .55; cursor: not-allowed; }
          .am-btn--primary {
            background: linear-gradient(135deg, #013d97, #0255cc); color: #fff;
            box-shadow: 0 2px 8px rgba(1,61,151,.30);
          }
          .am-btn--primary:hover:not(:disabled) { background: linear-gradient(135deg, #012f77, #0147aa); transform: translateY(-1px); }
          .am-btn--ghost { background: #f0f4fa; color: #5a6a8a; border: 1.5px solid #e2e8f0; width: auto; flex-shrink: 0; }
          .am-btn--ghost:hover { background: #e4ebf5; color: #013d97; }
          .am-btn--flex { flex: 1; width: auto; }
          .am-spinner {
            width: 15px; height: 15px;
            border: 2px solid rgba(255,255,255,.3); border-top-color: #fff;
            border-radius: 50%; animation: am-spin .6s linear infinite; flex-shrink: 0;
          }
          @keyframes am-spin { to { transform: rotate(360deg); } }
          .am-switch { font-size: 13px; color: #8895b0; text-align: center; }
          .am-switch__link {
            background: none; border: none; cursor: pointer;
            font-family: inherit; font-size: inherit; font-weight: 700;
            color: #013d97; padding: 0; text-decoration: underline;
          }
          .am-modal *:focus-visible { outline: 2px solid #029add; outline-offset: 2px; }

          /* ── RESPONSIVE ── */
          @media (max-width: 1024px) { .pd-related__grid, .pd-recent__grid { grid-template-columns: repeat(3, 1fr); } }
          @media (max-width: 1100px) { .pd-action-btn__label { display: none; } .pd-action-btn { padding: 8px 10px; } }
          @media (max-width: 768px) {
            .pd-hero { height: 140px; }
            .pd-hero__content { padding: 16px 20px; }
            .pd-toolbar { padding: 8px 16px; }
            .pd-main { padding: 24px 20px; grid-template-columns: 1fr; gap: 24px; }
            .pd-gallery { flex-direction: column-reverse; }
            .pd-thumbs { flex-direction: row; overflow-x: auto; }
            .pd-thumb { flex-shrink: 0; }
            .pd-tabs-wrap { padding: 0 20px; }
            .pd-tab-content { padding: 20px; }
            .pd-specs { grid-template-columns: 1fr; }
            .pd-related, .pd-recent { padding: 30px 0 60px; }
            .pd-related__inner, .pd-recent__inner { padding: 0 20px; }
            .pd-related__grid, .pd-recent__grid { grid-template-columns: repeat(2, 1fr); gap: 12px; }
            .pd-lightbox { padding: 20px; }
            .pd-wa-enquire { font-size: 12.5px; padding: 9px 14px; }
            /* Auth modal — always centered on mobile, never slide up from bottom */
            .am-backdrop { align-items: center; padding: 16px; }
            .am-modal { border-radius: 20px; max-height: 90vh; }
            .am-row { flex-direction: column; }
          }
          @media (max-width: 480px) {
            .pd-add-row { flex-wrap: wrap; }
            .pd-cta { flex: auto; width: 100%; order: -1; }
            .pd-related__grid, .pd-recent__grid { grid-template-columns: repeat(2, 1fr); }
            .pd-price-row { flex-direction: column; align-items: flex-start; gap: 10px; }
            .pd-wa-enquire { width: 100%; justify-content: center; }
            /* Auth modal — keep centered, allow scroll on very small screens */
            .am-backdrop { align-items: center; padding: 12px; }
            .am-modal { max-height: 88vh; }
          }

          a:focus-visible, button:focus-visible { outline: 2px solid #029add; outline-offset: 2px; }
          @media (prefers-reduced-motion: reduce) {
            .pd *, .pd *::before, .pd *::after { transition-duration: 0ms !important; animation-duration: 0ms !important; }
          }
        `}</style>
      </div>
      <Footer />
    </>
  );
}

export default ProductDetailPage;